import Decimal from 'decimal.js';
import { keyBy } from 'lodash';

import type { AiOnboardingModal_EntityFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import type { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  getLiabilityOwnerEntityFromSuggestionStatements,
  getOwningEntityFromSuggestionStatements,
  type SuggestionForKGV2Grouping,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import {
  getAISuggestionKindsForEntitySection,
  getSectionsForEntityType,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  PERSONAL_FAMILY_ACCOUNT_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  EstateOnboardingSuggestionGroup,
  SuggestionsByGroup,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import type { EstatePage_UnacknowledgedHoldingSuggestionFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import {
  AiSuggestionKind,
  type AiSuggestionWhereInput,
  type EntityKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

const EMPTY_GROUPS: SuggestionsByGroup = {
  [EstateOnboardingSuggestionGroup.DirectHoldings]: [],
  [EstateOnboardingSuggestionGroup.TrustEntities]: [],
  [EstateOnboardingSuggestionGroup.TrustHoldings]: [],
  [EstateOnboardingSuggestionGroup.BusinessEntities]: [],
  [EstateOnboardingSuggestionGroup.BusinessHoldings]: [],
};

const TRUST_AND_CHARITABLE_ENTITY_KINDS_SET = new Set([
  ...TRUST_ENTITY_KINDS,
  ...NON_TRUST_CHARITABLE_ENTITY_KINDS,
]);
const BUSINESS_ENTITY_KINDS_SET = new Set(BUSINESS_ENTITY_KINDS);
const PERSONAL_FAMILY_ACCOUNT_KINDS_SET = new Set(
  PERSONAL_FAMILY_ACCOUNT_KINDS
);

interface EstateSuggestionWithDeathOrder {
  kind: AiSuggestionKind;
  dpFirstDeathGrantorClientProfile?: { id: string } | null;
}

export function isSuggestionForPrimaryDeathOrder(
  suggestion: EstateSuggestionWithDeathOrder,
  primaryDeathOrderClientId?: string
) {
  return (
    suggestion.kind !== AiSuggestionKind.DpRecipientV2 ||
    !primaryDeathOrderClientId ||
    suggestion.dpFirstDeathGrantorClientProfile?.id ===
      primaryDeathOrderClientId
  );
}

export function isEstateEntitySuggestionReviewable(
  suggestion: EstateSuggestionWithDeathOrder,
  entityKind: EntityKind,
  primaryDeathOrderClientId?: string
) {
  if (isEstateHoldingSuggestion(suggestion)) return true;

  const entityType = getEntityTypeFromEntityKind(entityKind);
  const isShownInEntitySection = getSectionsForEntityType(entityType).some(
    (section) =>
      getAISuggestionKindsForEntitySection(entityType, section).includes(
        suggestion.kind
      )
  );

  return (
    isShownInEntitySection &&
    isSuggestionForPrimaryDeathOrder(suggestion, primaryDeathOrderClientId)
  );
}

export function getHoldingOwnerEntityIDFromSuggestion(
  suggestion: SuggestionForKGV2Grouping
): string {
  if (!suggestion.kgV2Statements?.edges?.length) {
    return '';
  }

  let ownerEntityID: string | null | undefined;

  switch (suggestion.kind) {
    case AiSuggestionKind.Asset:
    case AiSuggestionKind.LifeInsurance:
      ownerEntityID = getOwningEntityFromSuggestionStatements(suggestion);
      break;
    case AiSuggestionKind.Liability:
      ownerEntityID =
        getLiabilityOwnerEntityFromSuggestionStatements(suggestion);
      break;
    default:
      return '';
  }

  diagnostics.debug(
    '[EstateOnboarding] getHoldingOwnerEntityIDFromSuggestion V2 result',
    {
      suggestionId: suggestion.id,
      kind: suggestion.kind,
      ownerEntityID: ownerEntityID || '(not found)',
    }
  );

  return ownerEntityID || '';
}

/**
 * Groups estate onboarding AI suggestions into predefined categories based on their kind
 * and associated entity types.
 *
 * This Estate-page path is V2-only. Holdings use the explicit `entity` edge
 * written by the V2 save layer; statement traversal is not part of list-time
 * grouping or counting.
 *
 * @param suggestions - Array of AI suggestions to group
 * @param entities - Available entities to match against
 * @returns Suggestions grouped by estate onboarding category
 */
export function groupEstateOnboardingSuggestionsByGroup(
  suggestions: AiSuggestionForReviewCard[],
  entities: AiOnboardingModal_EntityFragment[]
): SuggestionsByGroup {
  const result: SuggestionsByGroup = {
    [EstateOnboardingSuggestionGroup.DirectHoldings]: [],
    [EstateOnboardingSuggestionGroup.TrustEntities]: [],
    [EstateOnboardingSuggestionGroup.TrustHoldings]: [],
    [EstateOnboardingSuggestionGroup.BusinessEntities]: [],
    [EstateOnboardingSuggestionGroup.BusinessHoldings]: [],
  };

  const entitiesMap = keyBy(entities, 'id');
  const unaccepted = suggestions.filter((s) => !s.acceptanceStatus);

  unaccepted.forEach((s) => {
    switch (s.kind) {
      case AiSuggestionKind.OnboardingTestamentaryEntity: {
        result[EstateOnboardingSuggestionGroup.TrustEntities].push(s);
        return;
      }
      case AiSuggestionKind.OnboardingEntity: {
        const entityKind = s.onboardingEntity?.entityKind;
        if (!entityKind) {
          diagnostics.debug(
            '[EstateOnboarding][groupSuggestions] Could not assign ENTITY suggestion to a group - no entity kind',
            { s }
          );
          return;
        }
        if (TRUST_AND_CHARITABLE_ENTITY_KINDS_SET.has(entityKind)) {
          result[EstateOnboardingSuggestionGroup.TrustEntities].push(s);
          return;
        }
        if (BUSINESS_ENTITY_KINDS_SET.has(entityKind)) {
          result[EstateOnboardingSuggestionGroup.BusinessEntities].push(s);
          return;
        }
        diagnostics.debug(
          '[EstateOnboarding][groupSuggestions] Could not assign ENTITY suggestion to a group - no UI in Direct Holdings tab for this entity kind',
          { suggestionId: s.id, entityKind }
        );
        return;
      }
      case AiSuggestionKind.Asset:
      case AiSuggestionKind.Liability:
      case AiSuggestionKind.LifeInsurance: {
        const ownerEntityID = s.entity?.id;
        const ownerEntity = ownerEntityID
          ? (entitiesMap[ownerEntityID] ?? null)
          : null;
        const group = getHoldingSuggestionGroup(ownerEntity);

        if (group) {
          result[group].push(s);
          return;
        }

        diagnostics.debug(
          '[EstateOnboarding][groupSuggestions] Could not assign holdings suggestion to a group',
          { suggestionId: s.id, kind: s.kind, ownerEntityID }
        );
        return;
      }
      default: {
        diagnostics.debug(
          '[EstateOnboarding][groupSuggestions] Irrelevant suggestion; not assigning to a group',
          { suggestionId: s.id, kind: s.kind }
        );
      }
    }
  });

  // Sort holdings suggestions by type and value
  // Order: Assets first (ascending by value), then Liabilities (descending by balance), then Life Insurance (ascending by death benefit)
  const sortHoldingsSuggestions = (
    suggestions: AiSuggestionForReviewCard[]
  ) => {
    const nonHoldings = suggestions.filter(
      (s) =>
        s.kind !== AiSuggestionKind.Asset &&
        s.kind !== AiSuggestionKind.Liability &&
        s.kind !== AiSuggestionKind.LifeInsurance
    );

    const assets = suggestions.filter((s) => s.kind === AiSuggestionKind.Asset);
    const liabilities = suggestions.filter(
      (s) => s.kind === AiSuggestionKind.Liability
    );
    const insurance = suggestions.filter(
      (s) => s.kind === AiSuggestionKind.LifeInsurance
    );

    // Sort assets ascending by assetValue (lowest to highest)
    const sortedAssets = assets.sort((a, b) =>
      new Decimal(a.asset?.assetValue || '0').comparedTo(
        new Decimal(b.asset?.assetValue || '0')
      )
    );
    // Sort liabilities ascending by outstandingBalance (lowest to highest)
    const sortedLiabilities = liabilities.sort((a, b) =>
      new Decimal(a.liability?.outstandingBalance || '0').comparedTo(
        new Decimal(b.liability?.outstandingBalance || '0')
      )
    );
    // Sort life insurance ascending by deathBenefitAmount (lowest to highest)
    const sortedInsurance = insurance.sort((a, b) =>
      new Decimal(a.lifeInsurance?.deathBenefitAmount || '0').comparedTo(
        new Decimal(b.lifeInsurance?.deathBenefitAmount || '0')
      )
    );

    return [
      ...nonHoldings,
      ...sortedAssets,
      ...sortedLiabilities,
      ...sortedInsurance,
    ];
  };

  result[EstateOnboardingSuggestionGroup.DirectHoldings] =
    sortHoldingsSuggestions(
      result[EstateOnboardingSuggestionGroup.DirectHoldings]
    );
  result[EstateOnboardingSuggestionGroup.TrustHoldings] =
    sortHoldingsSuggestions(
      result[EstateOnboardingSuggestionGroup.TrustHoldings]
    );
  result[EstateOnboardingSuggestionGroup.BusinessHoldings] =
    sortHoldingsSuggestions(
      result[EstateOnboardingSuggestionGroup.BusinessHoldings]
    );

  return result;
}

export function isEstateHoldingSuggestion<
  T extends Pick<AiSuggestionForReviewCard, 'kind'>,
>(
  suggestion: T
): suggestion is T & EstatePage_UnacknowledgedHoldingSuggestionFragment {
  return (
    suggestion.kind === AiSuggestionKind.Asset ||
    suggestion.kind === AiSuggestionKind.Liability ||
    suggestion.kind === AiSuggestionKind.LifeInsurance
  );
}

export function getUnacknowledgedHoldingsSuggestionsWhere(
  entityId: string
): AiSuggestionWhereInput {
  return {
    kindIn: [
      AiSuggestionKind.Asset,
      AiSuggestionKind.Liability,
      AiSuggestionKind.LifeInsurance,
    ],
    hasEntityWith: [{ id: entityId }],
    acceptanceStatusIsNil: true,
  };
}

/**
 * Determines which suggestion group a holding should belong to based on the
 * owner entity's kind.
 *
 * @param ownerEntity - The entity that owns the holding
 * @returns The suggestion group, or null if the entity kind doesn't match any group
 */
function getHoldingSuggestionGroup(
  ownerEntity: AiOnboardingModal_EntityFragment | null
): EstateOnboardingSuggestionGroup | null {
  if (!ownerEntity?.kind) return null;

  if (PERSONAL_FAMILY_ACCOUNT_KINDS_SET.has(ownerEntity.kind)) {
    return EstateOnboardingSuggestionGroup.DirectHoldings;
  }
  if (TRUST_AND_CHARITABLE_ENTITY_KINDS_SET.has(ownerEntity.kind)) {
    return EstateOnboardingSuggestionGroup.TrustHoldings;
  }
  if (BUSINESS_ENTITY_KINDS_SET.has(ownerEntity.kind)) {
    return EstateOnboardingSuggestionGroup.BusinessHoldings;
  }

  return null;
}

export { EMPTY_GROUPS };
